import { TYPE_COLORS } from './constants';

// Helper: Normalize any date to Local Time Midnight (00:00:00) to avoid timezone shifts
export const toLocalMidnight = (d: Date): Date => {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
};

// Helper: Strictly parse YYYY-MM-DD string to Local Midnight Date
export const parseInputDate = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
};

// Helper to parse Excel dates with STRICT normalization
export const parseExcelDate = (value: any): Date | null => {
  if (!value) return null;
  
  let d: Date | null = null;

  if (value instanceof Date) {
    d = value;
  } 
  // Excel Serial Date
  else if (typeof value === 'number') {
    // Excel base date logic adjustments
    d = new Date(Math.round((value - 25569) * 864e5));
  } 
  // String Parsing
  else {
    const strVal = String(value).trim();
    if (!strVal) return null;

    // Try standard parsing first
    let tempD = new Date(strVal);
    if (!isNaN(tempD.getTime()) && tempD.getFullYear() > 2000) {
      d = tempD;
    } else {
      // Fallback for Chinese formats (e.g., 11.14 or 11月14日)
      const currentYear = new Date().getFullYear();
      // Regex for MM.DD or MM月DD日
      const match = strVal.match(/(\d{1,2})[./月](\d{1,2})/);
      if (match) {
        tempD = new Date(currentYear, parseInt(match[1]) - 1, parseInt(match[2]));
        if (!isNaN(tempD.getTime())) {
          d = tempD;
        }
      }
    }
  }
  
  if (d) {
    return toLocalMidnight(d);
  }
  
  return null;
};

export const formatDate = (date: Date) => {
  return `${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
};

// UPDATED: Use UTC for calculation to prevent DST issues
export const getDaysDiff = (start: Date, end: Date) => {
  const s = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const e = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((e - s) / (1000 * 3600 * 24));
};

// Helper to determine day cell style and label
export const getDayConfig = (type: string, offset: number, isTentative: boolean, isFuture: boolean) => {
  let label = '';
  let title = '';
  let role = 'run'; // prep, open, run, rest, conversion, end, cut
  let dDay = offset - 3; // offset 3 is Day 0 (Open), offset 4 is Day 1
  
  if (offset < 3) {
    role = 'prep';
    label = `${offset + 1}`;
    title = `承接期 Day ${offset + 1}`;
  } else if (offset === 3) {
    role = 'open';
    label = '开';
    title = '开营日';
  } else {
    // 21 Day Logic
    if (type === '21Day') {
      if (dDay <= 21) {
        if (dDay === 21) { role = 'end'; label = '结'; title = '结营日'; }
        else if ([6, 11, 17].includes(dDay)) { role = 'rest'; label = `${dDay}`; title = '休息日'; }
        else if (dDay === 14) { role = 'conversion'; label = `${dDay}`; title = '一转节点'; }
        else { label = `${dDay}`; title = `运营期 Day ${dDay}`; }
      } else {
        role = 'cut'; label = '截'; title = '截单期';
      }
    } 
    // 15 Day / Others Logic
    else {
      if (dDay <= 15) {
        if (dDay === 15) { role = 'end'; label = '结'; title = '结营日'; }
        else if ([6, 12].includes(dDay)) { role = 'rest'; label = `${dDay}`; title = '休息日'; }
        else if (dDay === 8) { role = 'conversion'; label = `${dDay}`; title = '一转节点'; }
        else { label = `${dDay}`; title = `运营期 Day ${dDay}`; }
      } else {
        role = 'cut'; label = '截'; title = '截单期';
      }
    }
  }

  let style = '';
  const BASE_BG = isFuture ? 'bg-purple-50' : 'bg-white'; // FUTURE PURPLE BG LOGIC
  const BASE_BORDER = isFuture ? 'border-r border-purple-200' : 'border-r border-slate-100';

  if (isTentative) {
    // Predicted style
    const PRED_BORDER = 'border-r border-dashed border-slate-300';
    if (role === 'open') style = `bg-indigo-50 text-indigo-800 font-bold ${PRED_BORDER}`;
    else if (role === 'prep') style = `bg-slate-50 text-slate-400 ${PRED_BORDER}`;
    else if (role === 'cut') style = `bg-red-50 text-red-900 ${PRED_BORDER}`;
    else style = `bg-white text-indigo-400 ${PRED_BORDER}`;
  } else {
    // Normal style
    const textBase = TYPE_COLORS[type as keyof typeof TYPE_COLORS]?.text || 'text-gray-600';
    
    if (role === 'prep') style = `${BASE_BG} text-slate-300 ${BASE_BORDER}`;
    else if (role === 'open') style = `${BASE_BG} text-yellow-600 font-bold ${BASE_BORDER}`;
    else if (role === 'cut') style = `${BASE_BG} text-red-400 ${BASE_BORDER}`;
    else if (role === 'rest') style = `${BASE_BG} ${textBase} opacity-50 ${BASE_BORDER}`;
    else if (role === 'conversion') style = `${BASE_BG} ${textBase} font-bold underline decoration-yellow-400 underline-offset-2 ${BASE_BORDER}`;
    else if (role === 'end') style = `${BASE_BG} text-red-600 font-bold ${BASE_BORDER}`;
    else style = `${BASE_BG} ${textBase} ${BASE_BORDER}`;
  }

  return { label, style, title };
};
